import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFormContext,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { useEffect, useState, useMemo } from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FacilitySchedule } from "@/types/facility/schedule";
import { FormControl } from "@/components/ui/form";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import Combobox from "@/components/ui/common/combobox";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
interface FacilityScheduleGeneralInfoFormProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  trigger?: UseFormTrigger<FacilitySchedule>;
  action: "add" | "edit";
  open: boolean;
}

const colorOptions = [
  // Row 1
  { value: "red-500", label: "Red", class: "bg-red-500" },
  { value: "orange-500", label: "Orange", class: "bg-orange-500" },
  { value: "yellow-500", label: "Yellow", class: "bg-yellow-500" },
  { value: "green-500", label: "Green", class: "bg-green-500" },
  { value: "teal-500", label: "Teal", class: "bg-teal-500" },
  { value: "blue-500", label: "Blue", class: "bg-blue-500" },
  { value: "purple-500", label: "Purple", class: "bg-purple-500" },
  // Row 2
  { value: "red-600", label: "Dark Red", class: "bg-red-600" },
  { value: "orange-600", label: "Dark Orange", class: "bg-orange-600" },
  { value: "yellow-600", label: "Dark Yellow", class: "bg-yellow-600" },
  { value: "green-600", label: "Dark Green", class: "bg-green-600" },
  { value: "teal-600", label: "Dark Teal", class: "bg-teal-600" },
  { value: "blue-600", label: "Dark Blue", class: "bg-blue-600" },
  { value: "purple-600", label: "Dark Purple", class: "bg-purple-600" },
  // Row 3
  { value: "red-700", label: "Darker Red", class: "bg-red-700" },
  { value: "orange-700", label: "Darker Orange", class: "bg-orange-700" },
  { value: "yellow-700", label: "Darker Yellow", class: "bg-yellow-700" },
  { value: "green-700", label: "Darker Green", class: "bg-green-700" },
  { value: "teal-700", label: "Darker Teal", class: "bg-teal-700" },
  { value: "blue-700", label: "Darker Blue", class: "bg-blue-700" },
  { value: "purple-700", label: "Darker Purple", class: "bg-purple-700" },
];

const GeneralInfoForm = ({
  control,
  errors,
  watch,
  setValue,
  action,
  open,
}: FacilityScheduleGeneralInfoFormProps) => {
  // Use form context for better integration
  const {
    control: contextControl,
    formState: { errors: contextErrors },
    watch: contextWatch,
    setValue: contextSetValue,
    trigger: contextTrigger,
  } = useFormContext<FacilitySchedule>();

  // Use context versions if available, otherwise fall back to props
  const formControl = contextControl || control;
  const formErrors = contextErrors || errors;
  const formWatch = contextWatch || watch;
  const formSetValue = contextSetValue || setValue;
  const formTrigger = contextTrigger;

  const watcher = formWatch();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: facilityDataList } = useGetFacilitiesQuery({
    query:
      "status=active&bookable=true&load_add_ons=true&is_add_on=false&sort_key=id&sort_order=desc",
    orgId: orgId,
  });

  const { data: incomeCatData } = useGetIncomeCategoryQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc&status=active" },
    {
      skip: !orgId && !open,
    });

  const { data: taxGroupData } = useGetTaxGroupQuery({ query: "", org_id: orgId }, {
    skip: !orgId && !open,
  });


  const incomeCatOptions = useMemo(() => {
    return Array.isArray(incomeCatData?.data)
      ? incomeCatData?.data.map((incomeCat) => ({
        ...incomeCat,
        label: incomeCat?.name,
        value: incomeCat?.id?.toString() || "",
      }))
      : [];
  }, [incomeCatData]);

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.map((taxGroup) => ({
        ...taxGroup,
        label: taxGroup?.name,
        value: taxGroup?.id?.toString() || "",
      }))
      : [];
  }, [taxGroupData]);

  const facilityOptions = useMemo(() => {
    return Array.isArray(facilityDataList?.data)
      ? facilityDataList?.data.map((facility) => ({
        ...facility,
        label: facility.name || "",
        value: facility.id?.toString() || "",
      }))
      : [];
  }, [facilityDataList]);

  // Get selected facility's max capacity for validation
  const selectedFacilityCapacity = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityDataList?.data) {
      const facility = facilityDataList.data.find(
        (f) => f.id === selectedFacilityId
      );
      return facility?.max_capacity || null;
    }
    return null;
  }, [formWatch("general_info.facility_id"), facilityDataList]);

  // Get selected facility's booking type and member only status
  const selectedFacilityBookingInfo = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityDataList?.data) {
      const facility = facilityDataList.data.find(
        (f) => f.id === selectedFacilityId
      );
      return {
        bookingType: facility?.booking_type,
        isMemberOnlyFacility: facility?.booking_type === "Member Only",
      };
    }
    return { bookingType: null, isMemberOnlyFacility: false };
  }, [formWatch("general_info.facility_id"), facilityDataList]);

  // Fixed useEffect: watching for changes and using getFacilities API response
  useEffect(() => {
    const subscription = formWatch((value, { name }) => {
      if (name === "general_info.facility_id") {
        const facilityId = Number(value?.general_info?.facility_id);
        if (facilityId) {
          // Call facilityDataList from useGetFacilitiesQuery if already loaded
          if (facilityDataList?.data) {
            const facility = facilityDataList.data.find(
              (f) => f.id === facilityId
            );
            if (facility && facility.duration_per_slot) {
              formSetValue(
                "general_info.duration_per_slot",
                facility.duration_per_slot
              );
            }
            // Reset max_attendees if it exceeds the new facility's capacity
            const currentMaxAttendees = value?.general_info?.max_attendees;
            if (
              facility?.max_capacity &&
              currentMaxAttendees &&
              currentMaxAttendees > facility.max_capacity
            ) {
              formSetValue("general_info.max_attendees", facility.max_capacity);
            }
            // Handle member_only field based on facility booking type
            if (facility?.booking_type === "Member Only") {
              formSetValue("general_info.member_only", true);
            } else {
              formSetValue("general_info.member_only", false);
            }
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [formWatch, formSetValue, facilityDataList]);

  // Trigger validation when facility capacity changes
  useEffect(() => {
    if (formTrigger && selectedFacilityCapacity) {
      formTrigger("general_info.max_attendees");
    }
  }, [selectedFacilityCapacity, formTrigger]);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="font-medium text-sm">Facility Booking Details</p>
        <Separator className="h-[1px]" />
        {/* 1st row */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div>
            <Controller
              name="general_info.facility_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Facility <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={facilityOptions}
                    defaultValue={
                      facilityOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = facilityOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );

                      setValue("general_info.availability", {
                        sunday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                        monday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                        tuesday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                        wednesday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                        thursday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                        friday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                        saturday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
                      });
                    }}
                    label="Facility"
                    showLabel={false}
                    disabled={action === "edit"}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          {/* toggles */}
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2 mt-2">
              <Controller
                control={formControl}
                name="general_info.member_only"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={selectedFacilityBookingInfo.isMemberOnlyFacility}
                  />
                )}
              />
              <span
                className={`text-sm  ${selectedFacilityBookingInfo.isMemberOnlyFacility
                  ? "text-gray-500"
                  : ""
                  }`}
              >
                Members Only
              </span>
            </div>
          </div>
          <div>
            <Controller
              control={formControl}
              name="general_info.color"
              rules={{ required: "Required" }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <div>
                  <p className="text-sm">
                    Colour <span className="text-red-500">*</span>
                  </p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-between"
                      >
                        <div className="flex items-center">
                          {value && (
                            <div
                              className={`w-4 h-4 rounded-full mr-2 ${colorOptions.find((c) => c.value === value)
                                ?.class
                                }`}
                            ></div>
                          )}
                          <span className="font-normal text-sm">
                            {value
                              ? colorOptions.find((c) => c.value === value)
                                ?.label
                              : "Select Colour"}
                          </span>
                        </div>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Set Colour</h4>
                        <div className="grid grid-cols-7 gap-2">
                          {colorOptions.map((color, index) => (
                            <button
                              key={index}
                              type="button"
                              className={cn(
                                "w-6 h-6 rounded-full",
                                color.class,
                                value === color.value &&
                                "ring-2 ring-offset-2 ring-black"
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(color.value);
                              }}
                              title={color.label}
                            />
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div>
            <p className="text-sm">
              Max Attendees <span className="text-red-500">*</span>
            </p>
            <Controller
              control={formControl}
              name="general_info.max_attendees"
              rules={{
                required: "Required",
                min: {
                  value: 1,
                  message: "Minimum attendees is 1",
                },
                validate: (value) => {
                  if (
                    selectedFacilityCapacity &&
                    value &&
                    value > selectedFacilityCapacity
                  ) {
                    return `Max Attendees cannot exceed the facility capacity of ${selectedFacilityCapacity}.`;
                  }
                  return true;
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <Input
                    type="number"
                    id="general_info.max_attendees"
                    step="1"
                    min={1}
                    placeholder="Enter People"
                    value={value || ""}
                    onChange={(e) => onChange(Number(e.target.value))}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
        </div>
        {/* 2nd row */}
        <div className="grid grid-cols-4 gap-4 my-3">
          <div>
            <p className="text-sm font-normal">Location</p>
            <Controller
              control={formControl}
              name="general_info.location"
              rules={{
                maxLength: {
                  value: 200,
                  message: "Location cannot exceed 200 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Textarea
                    placeholder="Enter Location"
                    className="resize-none"
                    {...field}
                    rows={4}
                    value={field.value ?? ""}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <p className="text-sm font-normal">Description</p>
            <Controller
              control={formControl}
              name="general_info.description"
              rules={{
                maxLength: {
                  value: 1000,
                  message: "Description cannot exceed 1000 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Textarea
                    placeholder="Type your description here"
                    className="resize-none"
                    {...field}
                    rows={4}
                    value={field.value ?? ""}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <Controller
              name="general_info.income_category_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Income Category <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={incomeCatOptions}
                    defaultValue={
                      incomeCatOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = incomeCatOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );
                    }}
                    label="Income Category"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <Controller
              name="general_info.tax_group_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={formControl}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <p className="text-sm">
                    Tax Group <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={taxGroupOptions}
                    defaultValue={
                      taxGroupOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = taxGroupOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );
                    }}
                    label="Tax Group"
                    showLabel={false}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
          <div>
            <p className="text-sm">
              Advance Booking Days<span className="text-red-500">*</span>
            </p>
            <Controller
              control={formControl}
              name="general_info.advance_booking_days"
              rules={{
                required: "Required",
                min: {
                  value: 1,
                  message: "Minimum advance booking days is 1",
                },
              }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <>
                  <Input
                    type="number"
                    id="percentage"
                    step="1"
                    placeholder="Enter Booking Days"
                    value={value || ""}
                    onChange={onChange}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>



          <div className="flex flex-col justify-end">
            <span className=" p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
              Start Date{""}
              <span className="text-red-500">*</span>
            </span>
            <Controller
              name={"general_info.start_date" as keyof FacilitySchedule}
              rules={{ required: "Required" }}
              control={formControl}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => (
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <div className="relative">

                        <Button
                          variant={"outline"}
                          type="button"
                          className={cn(
                            "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]"
                          )}
                        >
                          {value ? (
                            format(
                              new Date(value as unknown as string),
                              "dd-MM-yyyy"
                            )
                          ) : (
                            <span>Select start date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                        {error && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {error.message}
                          </span>
                        )}
                      </div>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown-buttons"
                      selected={
                        value ? new Date(value as unknown as string) : undefined
                      }
                      defaultMonth={new Date()}
                      onSelect={(selectedDate) => {
                        if (selectedDate) {
                          onChange(format(selectedDate, "yyyy-MM-dd"));
                        }
                      }}
                      disabled={(date) =>
                        date.getTime() < new Date().setHours(0, 0, 0, 0)
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
          </div>
          <div className="flex flex-col justify-end">
            <span className=" p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
              End Date{""}
              <span className="text-red-500">*</span>
            </span>
            <Controller
              name={"general_info.end_date" as keyof FacilitySchedule}
              rules={{ required: "Required" }}
              control={formControl}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => (
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <div className="relative">

                        <Button
                          variant={"outline"}
                          type="button"
                          className={cn(
                            "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]"
                          )}
                        >
                          {value ? (
                            format(
                              new Date(value as unknown as string),
                              "dd-MM-yyyy"
                            )
                          ) : (
                            <span>Select End date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                        {error && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {error.message}
                          </span>
                        )}
                      </div>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <Calendar
                      mode="single"
                      captionLayout="dropdown-buttons"
                      selected={
                        value ? new Date(value as unknown as string) : undefined
                      }
                      defaultMonth={
                        formWatch("general_info.start_date")
                          ? new Date(formWatch("general_info.start_date"))
                          : new Date()
                      }
                      onSelect={(selectedDate) => {
                        if (selectedDate) {
                          onChange(format(selectedDate, "yyyy-MM-dd"));
                        }
                      }}
                      disabled={(date) => {
                        const startDateStr = formWatch(
                          "general_info.start_date"
                        );
                        const startDate = startDateStr
                          ? new Date(startDateStr)
                          : null;
                        const today = new Date(new Date().setHours(0, 0, 0, 0));
                        return startDate ? date < startDate : date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            />
          </div>

          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2">
              <Controller
                control={formControl}
                name="general_info.is_exclusive"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={(value) => {
                      field.onChange(value);
                      // Also set is_exclusive to maintain compatibility
                      formSetValue("general_info.is_exclusive", value);
                    }}
                  />
                )}
              />
              <span className="text-sm ">Exclusive Schedule</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GeneralInfoForm;
